import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  Filter, 
  Eye,
  ShoppingCart,
  IndianRupee,
  Calendar,
  Phone,
  Mail,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Package,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { API_BASE_URL } from '../../api';
import './AdminCustomerManagement.css';

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return `₹${numeric.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

const formatTitleCase = (value) => {
  if (!value) return '';
  const str = String(value).replace(/_/g, ' ').toLowerCase();
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const AdminCustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Fetch customer details
  const fetchCustomers = async (pageParam = currentPage, options = {}) => {
    try {
      setLoading(true);
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const pageToFetch = typeof options.page === 'number'
        ? options.page
        : (typeof pageParam === 'number' ? pageParam : 1);
      const limitToUse = typeof options.limit === 'number' ? options.limit : rowsPerPage;
      params.append('page', String(pageToFetch));
      params.append('limit', String(limitToUse));

      const response = await fetch(`${API_BASE_URL}/api/customerdetails?${params.toString()}`, {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
        if (data.pagination) {
          setPaginationInfo(data.pagination);
          const serverPage = parseInt(data.pagination.current_page, 10);
          if (!Number.isNaN(serverPage) && serverPage > 0) {
            setCurrentPage(serverPage);
          } else {
            setCurrentPage(pageToFetch);
          }
        } else {
          setPaginationInfo(null);
          setCurrentPage(pageToFetch);
        }
      } else {
        const error = await response.json();
        console.error('Error fetching customers:', error);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers(1);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchCustomers(1);
  }, [searchQuery, dateFrom, dateTo]);

  const toggleCustomerExpansion = (customerId) => {
    setExpandedCustomer(expandedCustomer === customerId ? null : customerId);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'placed': 'status-placed',
      'confirmed': 'status-confirmed',
      'packing': 'status-packing',
      'ready': 'status-ready',
      'out_for_delivery': 'status-out-for-delivery',
      'delivered': 'status-delivered',
      'cancelled': 'status-cancelled',
      'rejected': 'status-rejected'
    };
    return statusClasses[status] || 'status-default';
  };

  const totalCustomers = paginationInfo?.total_customers ?? customers.length;
  const totalPages = paginationInfo?.total_pages ?? (customers.length > 0 ? 1 : 0);

  return (
    <div className="admin-customer-management">
      <div className="admin-customer-header">
        <div className="header-content">
          <h1>Customer Management</h1>
          <p>View and manage all customer details and their order history</p>
        </div>
        <div className="header-actions">
          <div className="customer-stats">
            <div className="stat-item">
              <span className="stat-number">{totalCustomers}</span>
              <span className="stat-label">Total Customers</span>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => fetchCustomers(currentPage)}>
            <RefreshCw size={20} />
            Refresh
          </button>
        </div>
      </div>

      <div className="admin-customer-filters">
        <div className="filters-left">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCurrentPage(1);
                  fetchCustomers(1);
                }
              }}
              className="search-input"
            />
          </div>
        </div>

        <div className="filters-right">
          <div className="date-range">
            <input 
              type="date" 
              value={dateFrom} 
              onChange={(e) => setDateFrom(e.target.value)} 
              placeholder="From"
            />
            <span style={{ margin: '0 4px' }}>to</span>
            <input 
              type="date" 
              value={dateTo} 
              onChange={(e) => setDateTo(e.target.value)} 
              placeholder="To"
            />
          </div>
          <div className="results-count">
            {totalCustomers > 0
              ? `${customers.length} of ${totalCustomers} customers`
              : '0 customers'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading customers...</div>
      ) : (
        <div className="admin-customer-list">
          {customers.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <h3>No customers found</h3>
              <p>No customers match your current filters</p>
            </div>
          ) : (
            customers.map((customer) => (
              <div key={customer.customer_id} className="customer-card">
                <div className="customer-card-header" onClick={() => toggleCustomerExpansion(customer.customer_id)}>
                  <div className="customer-info">
                    <div className="customer-name-section">
                      <h3>{customer.name || 'Unknown Customer'}</h3>
                      <div className="customer-contact">
                        {customer.email && (
                          <span className="contact-item">
                            <Mail size={14} />
                            {customer.email}
                          </span>
                        )}
                        {customer.phone && (
                          <span className="contact-item">
                            <Phone size={14} />
                            {customer.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="customer-stats-section">
                    <div className="stat-box">
                      <ShoppingCart size={16} />
                      <span className="stat-value">{customer.total_orders}</span>
                      <span className="stat-label">Orders</span>
                    </div>
                    <div className="stat-box">
                      <IndianRupee size={16} />
                      <span className="stat-value">{formatCurrency(customer.total_spent)}</span>
                      <span className="stat-label">Total Spent</span>
                    </div>
                    <div className="stat-box">
                      <CheckCircle size={16} />
                      <span className="stat-value">{customer.completed_orders}</span>
                      <span className="stat-label">Completed</span>
                    </div>
                    <div className="stat-box">
                      <XCircle size={16} />
                      <span className="stat-value">{customer.cancelled_orders}</span>
                      <span className="stat-label">Cancelled</span>
                    </div>
                  </div>
                  <div className="customer-actions">
                    {expandedCustomer === customer.customer_id ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </div>
                </div>

                {expandedCustomer === customer.customer_id && (
                  <div className="customer-orders-section">
                    <div className="orders-header">
                      <h4>Order History</h4>
                      <span className="orders-count">{customer.orders?.length || 0} orders</span>
                    </div>
                    {customer.orders && customer.orders.length > 0 ? (
                      <div className="orders-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Order #</th>
                              <th>Vendor</th>
                              <th>Owner</th>
                              <th>Status</th>
                              <th>Payment</th>
                              <th>Items</th>
                              <th>Total</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customer.orders.map((order) => (
                              <React.Fragment key={order.id}>
                                <tr>
                                  <td>{order.order_number}</td>
                                  <td>{order.vendor_name || 'N/A'}</td>
                                  <td>{order.owner_name || 'N/A'}</td>
                                  <td>
                                    <span className={`status-badge ${getStatusBadge(order.status)}`}>
                                      {formatTitleCase(order.status)}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`payment-badge payment-${order.payment_status}`}>
                                      {formatTitleCase(order.payment_status)}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="items-info">
                                      <Package size={14} />
                                      {order.item_count || 0} items ({order.total_quantity || 0} qty)
                                    </span>
                                  </td>
                                  <td className="amount-cell">{formatCurrency(order.total_amount)}</td>
                                  <td>{formatDateTime(order.created_at)}</td>
                                </tr>
                                {order.items && order.items.length > 0 && (
                                  <tr className="order-items-row">
                                    <td colSpan="8">
                                      <div className="order-items-details">
                                        <div className="order-items-header">Order Items:</div>
                                        <div className="order-items-list">
                                          {order.items.map((item, idx) => (
                                            <div key={item.id || idx} className="order-item">
                                              <span className="item-name">
                                                {item.product_name || `Product #${item.product_id}`}
                                              </span>
                                              <span className="item-details">
                                                {item.quantity} x {formatCurrency(item.unit_price)} = {formatCurrency(item.total_price || (item.quantity * item.unit_price))}
                                              </span>
                                              {item.product_sku && (
                                                <span className="item-sku">SKU: {item.product_sku}</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="no-orders">
                        <p>No orders found for this customer</p>
                      </div>
                    )}
                    {customer.first_order_date && (
                      <div className="customer-timeline">
                        <div className="timeline-item">
                          <Calendar size={14} />
                          <span>First Order: {formatDateTime(customer.first_order_date)}</span>
                        </div>
                        {customer.last_order_date && customer.last_order_date !== customer.first_order_date && (
                          <div className="timeline-item">
                            <Calendar size={14} />
                            <span>Last Order: {formatDateTime(customer.last_order_date)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}

          {customers.length > 0 && (
            <div className="pagination-container">
              <div style={{ fontSize: 14, color: '#666' }}>
                Showing {customers.length} of {totalCustomers} customers (page {currentPage} of {Math.max(totalPages, 1)})
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage > 1) {
                      const prevPage = currentPage - 1;
                      setCurrentPage(prevPage);
                      fetchCustomers(prevPage);
                    }
                  }}
                  disabled={currentPage <= 1}
                >
                  Prev
                </button>
                {(() => {
                  const pages = [];
                  const maxButtons = 7;
                  if (totalPages <= maxButtons) {
                    for (let i = 1; i <= totalPages; i += 1) {
                      pages.push(i);
                    }
                  } else {
                    const setPages = new Set([1, totalPages, currentPage]);
                    for (let i = 1; i <= 2; i += 1) {
                      setPages.add(currentPage - i);
                      setPages.add(currentPage + i);
                    }
                    const sortedPages = Array.from(setPages)
                      .filter(page => page >= 1 && page <= totalPages)
                      .sort((a, b) => a - b);

                    let lastPage = 0;
                    sortedPages.forEach((page) => {
                      if (lastPage && page - lastPage > 1) {
                        pages.push('ellipsis');
                      }
                      pages.push(page);
                      lastPage = page;
                    });
                  }
                  return pages.map((page, index) => {
                    if (page === 'ellipsis') {
                      return (
                        <span key={`ellipsis-${index}`} style={{ padding: '4px 8px', color: '#999' }}>...</span>
                      );
                    }
                    return (
                      <button
                        key={`page-${page}`}
                        className={`btn btn-secondary btn-sm ${page === currentPage ? 'active' : ''}`}
                        style={page === currentPage ? { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' } : {}}
                        onClick={() => {
                          if (page !== currentPage) {
                            setCurrentPage(page);
                            fetchCustomers(page);
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  });
                })()}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      const nextPage = currentPage + 1;
                      setCurrentPage(nextPage);
                      fetchCustomers(nextPage);
                    }
                  }}
                  disabled={currentPage >= totalPages || totalPages === 0}
                >
                  Next
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#666' }}>Rows per page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value, 10) || 10;
                    setRowsPerPage(newSize);
                    setCurrentPage(1);
                    fetchCustomers(1, { limit: newSize, page: 1 });
                  }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd' }}
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCustomerManagement;

